import { useFacilityMap } from '@/features/_hooks/use-facility-map'
import { LIMIT_PER_STATUS, useKanbanCardsQuery } from '@/features/_hooks/use-kanban-cards-query'
import { useSearchParams } from '@/features/_hooks/use-search-params'
import { mutations } from '@/features/_lib/react-query'
import type { DragEndEvent, DragStartEvent } from '@dnd-kit/core'
import { ApplicationStatusSchema, type TApplicationStatus } from '@doutorsim/shared/applications'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useCallback, useMemo, useRef, useState } from 'react'
import {
  KanbanBoard,
  KanbanCard,
  KanbanCards,
  KanbanHeader,
  KanbanProvider,
} from '../../../_components/ui/kibo-ui/kanban'
import { getStatusConfig, getStatusOptions } from '../../_constants/admission-status'
import type { TStaffApplicationListItem } from '../../_lib/api/entities/applications.staff'
import type { TUserType } from '../../_lib/auth-client.types'
import { useCompanyStore } from '../../_stores'
import { ApplicationKanbansCard } from './application-kanban-card'
import { ApplicationKanbanCardSkeleton } from './application-kanban-card-skeleton'

type KanbanApplication = TStaffApplicationListItem & {
  name: string
  column: string
  facilityName?: string
}

export interface ApplicationKanbanProps {
  mode: 'staff' | 'company' | 'facility'
  facilityId?: string
  companyId?: string
  userType: TUserType
  canChangeStatus?: boolean
  search?: string
  status?: TApplicationStatus[]
  startDate?: string
  endDate?: string
  showEmptyColumns?: boolean
  filterFacilityIds?: string[]
}

export function ApplicationKanban({
  mode,
  facilityId,
  companyId,
  userType,
  canChangeStatus = false,
  endDate,
  search,
  startDate,
  status,
  showEmptyColumns = false,
  filterFacilityIds,
}: ApplicationKanbanProps) {
  const dragStartColumn = useRef<string | null>(null)
  const [manuallyExpanded, setManuallyExpanded] = useState<Set<string>>(new Set())
  const { setSearchParam } = useSearchParams()
  const navigate = useNavigate()
  const companySlug = useCompanyStore((s) => s.company?.slug)

  const queryClient = useQueryClient()

  const {
    columns: columnsData,
    getColumn,
    isLoading,
    isFetching,
    prevColumnCounts,
    fetchMore,
    isFetchingMore,
  } = useKanbanCardsQuery({
    mode,
    companyId,
    facilityId,
    search,
    status,
    startDate,
    endDate,
    filterFacilityIds,
  })
  const facilityMap = useFacilityMap(mode, companyId)

  const updateStatusMutation = useMutation(mutations.staffApplications.updateStatus(queryClient))

  const allStatuses = useMemo(() => getStatusOptions(userType), [userType])

  const statusFilterSet = useMemo(
    () => (status && status.length > 0 ? new Set<string>(status) : null),
    [status],
  )

  const applications = useMemo<KanbanApplication[]>(
    () =>
      Object.entries(columnsData).flatMap(([columnStatus, column]) =>
        column.data.map((application) => ({
          ...application,
          name: application.person?.name ?? 'Unknown',
          column: columnStatus,
          facilityName: facilityMap?.get(application.facilityId)?.name,
        })),
      ),
    [columnsData, facilityMap],
  )

  const columns = useMemo(
    () =>
      allStatuses.map((opt) => {
        const config = getStatusConfig(opt.value, userType)
        return { id: opt.value, name: config.label, color: config.color }
      }),
    [allStatuses, userType],
  )

  const populatedColumns = useMemo(
    () =>
      new Set<string>(
        Object.entries(columnsData)
          .filter(([_, col]) => col.data.length > 0)
          .map(([s]) => s),
      ),
    [columnsData],
  )

  const [prevPopulatedColumns, setPrevPopulatedColumns] = useState<Set<string>>(new Set())
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false)

  if (!isLoading && !isFetching && !hasFetchedOnce) {
    setHasFetchedOnce(true)
  }

  if (!isLoading && populatedColumns !== prevPopulatedColumns) {
    setPrevPopulatedColumns(populatedColumns)
  }

  const showSkeleton = isLoading || isFetching
  const isInitialLoad = showSkeleton && !hasFetchedOnce
  const effectivePopulatedColumns = showSkeleton ? prevPopulatedColumns : populatedColumns

  const isColumnCollapsed = useCallback(
    (columnId: string) => {
      if (statusFilterSet && !statusFilterSet.has(columnId)) return true
      if (isInitialLoad) return false
      if (
        !showEmptyColumns &&
        !effectivePopulatedColumns.has(columnId) &&
        !manuallyExpanded.has(columnId)
      )
        return true
      return false
    },
    [isInitialLoad, statusFilterSet, showEmptyColumns, effectivePopulatedColumns, manuallyExpanded],
  )

  const gridTemplateColumns = useMemo(
    () => columns.map((col) => (isColumnCollapsed(col.id) ? '40px' : '280px')).join(' '),
    [columns, isColumnCollapsed],
  )

  const updateStatusFilter = useCallback(
    (statuses: TApplicationStatus[]) => {
      const allSelected = statuses.length >= allStatuses.length
      setSearchParam('status', allSelected ? undefined : statuses.join(','))
    },
    [setSearchParam, allStatuses],
  )

  const handleColumnCollapse = useCallback(
    (columnId: string) => {
      setManuallyExpanded((prev) => {
        if (!prev.has(columnId)) return prev
        const next = new Set(prev)
        next.delete(columnId)
        return next
      })
      const current = status && status.length > 0 ? status : allStatuses.map((s) => s.value)
      const next = current.filter((s) => s !== columnId)
      updateStatusFilter(next)
    },
    [status, allStatuses, updateStatusFilter],
  )

  const handleColumnExpand = useCallback(
    (columnId: string) => {
      const parsed = ApplicationStatusSchema.safeParse(columnId)
      if (!parsed.success) return

      setManuallyExpanded((prev) => {
        const next = new Set(prev)
        next.add(columnId)
        return next
      })
      const current = status ?? []
      updateStatusFilter([...current, parsed.data])
    },
    [status, updateStatusFilter],
  )

  const handleCardClick = useCallback(
    (application: KanbanApplication) => {
      if (mode === 'staff') {
        navigate({
          to: '/applications/$applicationId/profile',
          params: { applicationId: application.id },
          search: { companyId: application.companyId, facilityId: application.facilityId },
        })
      } else if (companySlug) {
        navigate({
          to: '/companies/$companySlug/applications/$applicationId/profile',
          params: { companySlug, applicationId: application.id },
        })
      }
    },
    [mode, navigate, companySlug],
  )

  // Drag-and-drop requires mutable local state
  const [dragApplications, setDragApplications] = useState<KanbanApplication[] | null>(null)
  const kanbanData = dragApplications ?? applications

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      const card = kanbanData.find((r) => r.id === event.active.id)
      if (card) {
        dragStartColumn.current = card.column ?? null
        setDragApplications([...kanbanData])
      }
    },
    [kanbanData],
  )

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event

      if (!over) {
        dragStartColumn.current = null
        setDragApplications(null)
        return
      }

      const movedCard = kanbanData.find((r) => r.id === active.id)

      if (movedCard && canChangeStatus && dragStartColumn.current) {
        const finalColumn = movedCard.column
        const parsed = ApplicationStatusSchema.safeParse(finalColumn)

        if (dragStartColumn.current !== finalColumn && parsed.success) {
          updateStatusMutation.mutate({
            applicationId: movedCard.id,
            status: parsed.data,
          })
        }
      }

      dragStartColumn.current = null
      setDragApplications(null)
    },
    [kanbanData, userType, updateStatusMutation],
  )

  const handleDataChange = useCallback((newData: KanbanApplication[]) => {
    setDragApplications(newData)
  }, [])

  return (
    <div className="relative min-h-0 w-full flex-1">
      <div className="absolute inset-0">
        <KanbanProvider
          className="min-h-0 flex-1 pl-14 pr-14"
          style={{ gridTemplateColumns }}
          columns={columns}
          data={kanbanData}
          onDataChange={handleDataChange}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          {(column) => {
            const isCollapsed = isColumnCollapsed(column.id)

            return (
              <KanbanBoard
                className="w-full overflow-hidden rounded-lg border-0 bg-brand-grayscale-2 shadow-none"
                id={column.id}
                key={column.id}
              >
                <KanbanHeader className="border-b-0 bg-brand-grayscale-2 px-4 pb-2 pt-4">
                  <div
                    className={`flex items-center gap-2 select-none ${isCollapsed ? 'flex-col' : ''}`}
                  >
                    <div
                      className="h-2 w-2 shrink-0 rounded-full"
                      style={{ backgroundColor: String(column.color) }}
                    />
                    <span
                      className={`cursor-pointer font-semibold whitespace-nowrap hover:underline ${isCollapsed ? 'text-xs text-brand-grayscale-6' : 'text-sm text-brand-grayscale-8'}`}
                      onClick={() =>
                        isCollapsed
                          ? handleColumnExpand(column.id)
                          : handleColumnCollapse(column.id)
                      }
                      style={isCollapsed ? { writingMode: 'vertical-lr' } : undefined}
                    >
                      {column.name}
                    </span>
                    {!isCollapsed && !showSkeleton && (
                      <span className="text-xs font-medium text-brand-grayscale-6">
                        {getColumn(column.id).meta.totalCount ?? 0}
                      </span>
                    )}
                  </div>
                </KanbanHeader>
                {!isCollapsed &&
                  (showSkeleton ? (
                    <div className="flex flex-col gap-2 px-2 pb-2">
                      {Array.from({
                        length: isInitialLoad ? LIMIT_PER_STATUS : prevColumnCounts[column.id] || 3,
                      }).map((_, i) => (
                        <div
                          key={i}
                          className="rounded-lg border bg-white p-0 shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]"
                        >
                          <ApplicationKanbanCardSkeleton />
                        </div>
                      ))}
                    </div>
                  ) : (
                    <KanbanCards<KanbanApplication>
                      id={column.id}
                      footer={
                        getColumn(column.id).meta.hasMore ? (
                          <div className="px-3 pb-3">
                            <button
                              className="w-full rounded-md py-1.5 text-center text-xs font-medium text-brand-grayscale-8 hover:bg-brand-grayscale-3 disabled:opacity-50"
                              onClick={() => fetchMore(column.id)}
                              disabled={isFetchingMore(column.id)}
                            >
                              {isFetchingMore(column.id) ? 'Carregando...' : 'Ver mais'}
                            </button>
                          </div>
                        ) : undefined
                      }
                    >
                      {(application) => (
                        <KanbanCard
                          column={column.id}
                          id={application.id}
                          key={application.id}
                          name={application.name}
                          className="p-0 shadow-[0_1px_3px_0_rgba(0,0,0,0.06)]"
                        >
                          <div
                            className="cursor-default"
                            onClick={() => {
                              if (!dragStartColumn.current) {
                                handleCardClick(application)
                              }
                            }}
                          >
                            <ApplicationKanbansCard application={application} userType={userType} />
                          </div>
                        </KanbanCard>
                      )}
                    </KanbanCards>
                  ))}
              </KanbanBoard>
            )
          }}
        </KanbanProvider>
      </div>
    </div>
  )
}
